import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { connect } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Link,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import StandardHeader from "../../components/global/StandardHeader";
import { TYPE_COLOR } from "./outcomes";
import { fromUtc } from "./DetailParts";

const shortDate = (date) => (date ? moment(date, "YYYY-MM-DD").format("MMM D, YYYY") : "—");
const when = (utc) => {
  const at = fromUtc(utc);
  return at ? at.format("MMM D, YYYY h:mm A") : "—";
};
const money = (n) => Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
// Newest first by the chosen date. The date range always filters on the processed date.
const SORTS = { processed: "dateProcessed", created: "created", moveDate: "moveDate" };

function ProcessedRow({ move }) {
  const attention = move.outcome === "attention";
  return (
    <Box sx={{ borderTop: "1px solid", borderColor: "divider", py: 1.25, px: 1.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "34px minmax(0, 1fr) auto", columnGap: 1.25, alignItems: "start" }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: TYPE_COLOR[move.kind], pt: 0.25 }}>{move.kind === "in" ? "IN" : "OUT"}</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            <Link component={RouterLink} to={`/move-ins-outs/${move.kind}/${move.uuid}`} underline="hover" color="inherit">{move.name || "—"}</Link>
            <Box component="span" sx={{ fontWeight: 400, color: "text.secondary" }}>
              {` · ${move.kind === "in" ? "moved in" : "moved out"} ${shortDate(move.moveDate)}`}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            {[move.propertyName, move.unitAddress].filter(Boolean).join(" · ") || "No unit linked"}
            {move.accountNum ? ` · #${move.accountNum}` : ""}
            {move.email ? ` · ${move.email}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.25 }}>Processed {when(move.dateProcessed)} by {move.processedBy}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {move.source || "Source unknown"} · created {when(move.created)}{move.createdByName ? ` by ${move.createdByName}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12, color: attention ? "#8a2020" : "text.secondary" }}>
            {move.label && move.label !== "Processed" && !move.label.startsWith("Processed ") ? `${move.label} · ` : ""}{move.reason}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {move.bill != null ? money(move.bill) : ""}
        </Typography>
      </Box>
    </Box>
  );
}

// Moves leave the board the day after they're processed (once their final bill is out). This lists them by the day
// they were processed.
function ProcessedMovesPage(props) {
  const details = props.user?.details;
  const canView = Boolean(details && (details.billing || details.administration || details.isSuperUser));
  const [range, setRange] = useState({
    startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  // The server searches (and filters by who processed them) before its 500-a-kind limit, so a match older than the
  // newest 500 is still found. It searches once typing pauses.
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [who, setWho] = useState("all");
  const [sortBy, setSortBy] = useState("processed");

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!canView) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    const params = { ...range, search: query || undefined, processedBy: who === "all" ? undefined : who };
    axios.get("/moves-dashboard/processed", { params })
      .then((response) => { if (active) setData(response.data); })
      .catch((err) => { if (active) setError(err?.response?.data?.message || "Couldn't load processed moves."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [canView, range.startDate, range.endDate, query, who]); // eslint-disable-line react-hooks/exhaustive-deps

  const moves = useMemo(() => {
    const field = SORTS[sortBy];
    return (data?.moves || [])
      .filter((m) => kind === "all" || m.kind === kind)
      .sort((a, b) => String(b[field] || "").localeCompare(String(a[field] || "")));
  }, [data, kind, sortBy]);

  if (!details) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title="Processed moves" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      </NavFrame>
    );
  }
  if (!canView) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title="Processed moves" />
        <Divider className="mb-3 mt-3" />
        <h3>You do not have privileges for the moves dashboard</h3>
      </NavFrame>
    );
  }

  return (
    <NavFrame page="Moves Dashboard">
      <StandardHeader title="Processed moves" />
      <Divider className="mb-3 mt-3" />
      <Link component={RouterLink} to="/move-ins-outs" sx={{ fontSize: 13 }}>← Moves dashboard</Link>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", my: 2 }}>
        <TextField size="small" placeholder="Search name, email, property, unit, account" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260, flex: "1 1 260px" }} />
        <TextField size="small" type="date" label="Processed from" InputLabelProps={{ shrink: true }} value={range.startDate} onChange={(e) => e.target.value && setRange((r) => ({ ...r, startDate: e.target.value }))} />
        <TextField size="small" type="date" label="to" InputLabelProps={{ shrink: true }} value={range.endDate} onChange={(e) => e.target.value && setRange((r) => ({ ...r, endDate: e.target.value }))} />
        <ToggleButtonGroup size="small" exclusive value={kind} onChange={(e, v) => v && setKind(v)}>
          <ToggleButton value="all" sx={{ textTransform: "none" }}>All</ToggleButton>
          <ToggleButton value="in" sx={{ textTransform: "none" }}>Move-ins</ToggleButton>
          <ToggleButton value="out" sx={{ textTransform: "none" }}>Move-outs</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={who} onChange={(e, v) => v && setWho(v)}>
          <ToggleButton value="all" sx={{ textTransform: "none" }}>Anyone</ToggleButton>
          <ToggleButton value="batch" sx={{ textTransform: "none" }}>The batch</ToggleButton>
          <ToggleButton value="analyst" sx={{ textTransform: "none" }}>Analysts</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={sortBy} onChange={(e, v) => v && setSortBy(v)} aria-label="Sort, newest first">
          <ToggleButton value="processed" sx={{ textTransform: "none" }}>Processed date</ToggleButton>
          <ToggleButton value="created" sx={{ textTransform: "none" }}>Created date</ToggleButton>
          <ToggleButton value="moveDate" sx={{ textTransform: "none" }}>Move date</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {data?.truncated ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.search || data.processedBy
            ? "Showing the latest 500 of each kind that match. Narrow the dates or the search to see older ones."
            : "Showing the latest 500 of each kind. Search, or narrow the dates, to see older ones."}
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", px: 1.5, py: 1 }}>
          {loading ? "Loading…" : `${moves.length} processed move${moves.length === 1 ? "" : "s"}`}
        </Typography>
        {!loading && !moves.length ? (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", px: 1.5, pb: 2 }}>No processed moves match.</Typography>
        ) : null}
        {moves.map((m) => <ProcessedRow key={`${m.kind}-${m.uuid}`} move={m} />)}
      </Paper>
    </NavFrame>
  );
}

function mapStateToProps(state) {
  return { user: state.user };
}

export default connect(mapStateToProps)(ProcessedMovesPage);
